import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { RefreshCw, Loader2, ArrowRight } from "lucide-react";
import { auth, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Aurora, Logo, FireButton } from "@/components/fx";

const Captcha = ({ value, onChange, onId, nonce }) => {
  const [c, setC] = useState(null);
  const loaded = useRef(null);
  const load = useCallback(() => auth.captcha().then((d) => { setC(d); onId(d.captcha_id); onChange(""); }).catch(() => setC(null)), [onId, onChange]);
  useEffect(() => { if (loaded.current === nonce) return; loaded.current = nonce; load(); }, [load, nonce]);
  return (
    <div>
      <label className="label-mono text-white/50">Verify you are human</label>
      <div className="mt-2 flex items-center gap-2">
        {c ? <img data-testid="captcha-image" data-captcha-id={c.captcha_id} src={c.image} alt="captcha challenge" className="h-[54px] rounded-lg border border-white/10" /> : <div className="h-[54px] w-[156px] animate-pulse rounded-lg bg-white/5" />}
        <button type="button" data-testid="captcha-refresh" onClick={load} className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-white" aria-label="New captcha"><RefreshCw className="h-4 w-4" /></button>
        <input data-testid="captcha-input" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} maxLength={6} placeholder="Type the code" className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-3 font-mono tracking-[0.3em] text-white outline-none focus:border-[#7a5cff]" />
      </div>
    </div>
  );
};

const score = (p) => [p.length >= 10, /[A-Z]/.test(p) && /[a-z]/.test(p), /\d/.test(p), /[^A-Za-z0-9]/.test(p), p.length >= 14].filter(Boolean).length;
const Strength = ({ pw }) => {
  const s = score(pw);
  const label = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"][s];
  const col = ["#ff5c7a", "#ff5c7a", "#f59e0b", "#22d3ee", "#6fd6b0", "#6fd6b0"][s];
  return (
    <div data-testid="password-strength" className="mt-2">
      <div className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300" style={{ background: i < s ? col : "rgba(255,255,255,.1)" }} />)}</div>
      <div className="mt-1 text-xs" style={{ color: col }}>{pw ? `${label}${pw.length < 10 ? " · min 10 characters" : ""}` : "Min 10 characters"}</div>
    </div>
  );
};

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="label-mono text-white/50">{label}</span>
    <input {...p} className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#7a5cff]" />
  </label>
);

const AuthForm = ({ mode }) => {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [cid, setCid] = useState("");
  const [ans, setAns] = useState("");
  const [nonce, setNonce] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const signup = mode === "signup";
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (signup && f.password.length < 10) return setErr("Password must be at least 10 characters.");
    if (signup && !terms) return setErr("Please accept the Terms of Service and Privacy Policy.");
    setBusy(true);
    try {
      const body = { email: f.email, password: f.password, captcha_id: cid, captcha_answer: ans, ...(signup ? { name: f.name, accept_terms: terms } : {}) };
      setUser(await (signup ? auth.signup(body) : auth.login(body)));
      nav("/app");
    } catch (e2) {
      setErr(errMsg(e2));
      setNonce((n) => n + 1);
    } finally { setBusy(false); }
  };
  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#07060b] px-4 py-12">
      <Aurora />
      <motion.form data-testid={`${mode}-form`} onSubmit={submit} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-strong noise relative w-full max-w-md space-y-5 rounded-3xl p-8">
        <Logo />
        <div>
          <h1 className="font-display text-3xl font-bold text-white">{signup ? "Create your FORGE account" : "Welcome back"}</h1>
          <p className="mt-1 text-sm text-white/55">{signup ? "Your workspace is ready in seconds." : "Log in to your command center."}</p>
        </div>
        {signup && <Field label="Name" data-testid="signup-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ada Lovelace" />}
        <Field label="Email" data-testid={`${mode}-email-input`} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@company.com" />
        <div>
          <Field label="Password" data-testid={`${mode}-password-input`} type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="••••••••••" />
          {signup && <Strength pw={f.password} />}
        </div>
        <Captcha value={ans} onChange={setAns} onId={setCid} nonce={nonce} />
        {signup && (
          <label className="flex items-start gap-2 text-xs text-white/60">
            <input data-testid="signup-terms-checkbox" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 accent-[#7a5cff]" />
            <span>I agree to the <Link to="/legal/terms" target="_blank" data-testid="signup-terms-link" className="text-[#22d3ee]">Terms of Service</Link>, <Link to="/legal/privacy" target="_blank" data-testid="signup-privacy-link" className="text-[#22d3ee]">Privacy Policy</Link> and <Link to="/legal/acceptable-use" target="_blank" className="text-[#22d3ee]">Acceptable Use Policy</Link>.</span>
          </label>
        )}
        {err && <div data-testid={`${mode}-error`} className="rounded-xl border border-[#ff5c7a]/40 bg-[#ff5c7a]/10 px-3 py-2 text-sm text-[#ff9aac]">{err}</div>}
        <FireButton type="submit" disabled={busy} data-testid={`${mode}-submit-button`} className="w-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{signup ? "Create account" : "Log in"} <ArrowRight className="h-4 w-4" /></>}
        </FireButton>
        <p className="text-center text-sm text-white/50">
          {signup ? "Already have an account? " : "New to FORGE? "}
          <Link to={signup ? "/login" : "/signup"} data-testid={`${mode}-switch-link`} className="font-semibold text-[#22d3ee]">{signup ? "Log in" : "Create an account"}</Link>
        </p>
      </motion.form>
    </div>
  );
};

export const Login = () => <AuthForm mode="login" />;
export const Signup = () => <AuthForm mode="signup" />;
